import { useState, useEffect } from 'react';
import { methodologyAPI } from '../lib/api';
import { Shield, Eye, Flag, MessageSquare, Trash2, Plus, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const VERDICT_COPY = {
  not_flagged: { label: 'NOT FLAGGED', color: '#2F8A92', note: 'Platform did not act.' },
  flagged_low: { label: 'FLAGGED · LOW', color: '#D4A84A', note: 'Logged for pattern review.' },
  flagged_medium: { label: 'FLAGGED · MEDIUM', color: '#FF7A00', note: 'Held in review queue.' },
  flagged_high: { label: 'FLAGGED · HIGH', color: '#FF4444', note: 'Auto-strike pending admin confirm.' },
};

function ExampleCard({ example }) {
  const v = VERDICT_COPY[example.verdict] || VERDICT_COPY.not_flagged;
  return (
    <div
      className="border rounded p-4 space-y-2"
      style={{ borderColor: `${v.color}33` }}
      data-testid={`mod-example-${example.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-hud text-[10px] tracking-widest uppercase" style={{ color: v.color }}>
          {v.label}
        </span>
        {example.categories?.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {example.categories.map((c) => (
              <span key={c} className="font-hud text-[9px] tracking-wider uppercase px-2 py-0.5 rounded border border-[#52525B]/40 text-white/80">
                {c.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="border-l-2 border-[#D4A84A]/30 pl-3">
        <p className="font-historical text-xs text-[#D4A84A] tracking-wide">{example.redacted_summary}</p>
      </div>
      <div>
        <p className="font-hud text-[9px] tracking-wider uppercase text-[#2F8A92] mb-1">AI RATIONALE</p>
        <p className="font-mono text-[11px] text-white/80 leading-relaxed italic">"{example.rationale}"</p>
      </div>
      {example.methodology_note && (
        <div>
          <p className="font-hud text-[9px] tracking-wider uppercase text-[#D4A84A] mb-1">WHY THIS TEACHES</p>
          <p className="font-mono text-[11px] text-[#e8d5a3] leading-relaxed">{example.methodology_note}</p>
        </div>
      )}
      <p className="font-mono text-[9px] text-[#52525B]">
        Curated by {example.curated_by} · {new Date(example.created_at).toLocaleDateString()}
      </p>
    </div>
  );
}

function CurateForm({ onCurated, onCancel }) {
  const [form, setForm] = useState({
    redacted_summary: '',
    verdict: 'not_flagged',
    categories: '',
    rationale: '',
    methodology_note: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handle = async () => {
    setSubmitting(true);
    try {
      await methodologyAPI.curateExample({
        redacted_summary: form.redacted_summary,
        verdict: form.verdict,
        categories: form.categories.split(',').map((s) => s.trim()).filter(Boolean),
        rationale: form.rationale,
        methodology_note: form.methodology_note,
      });
      onCurated();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#D4A84A]/30 rounded p-4 space-y-2" data-testid="curate-form">
      <h4 className="font-hud text-sm tracking-widest uppercase text-[#D4A84A]">NEW EXAMPLE</h4>
      <input
        data-testid="curate-summary"
        className="hud-input text-sm"
        placeholder='Redacted summary (e.g., "User cited disputed casualty figure")'
        value={form.redacted_summary}
        onChange={(e) => setForm({ ...form, redacted_summary: e.target.value })}
      />
      <select
        data-testid="curate-verdict"
        className="hud-input text-sm w-full"
        value={form.verdict}
        onChange={(e) => setForm({ ...form, verdict: e.target.value })}
      >
        {Object.entries(VERDICT_COPY).map(([k, v]) => (
          <option key={k} value={k}>{v.label}</option>
        ))}
      </select>
      <input
        data-testid="curate-categories"
        className="hud-input text-sm"
        placeholder="Categories (comma-separated, e.g., harassment, pii_leakage)"
        value={form.categories}
        onChange={(e) => setForm({ ...form, categories: e.target.value })}
      />
      <textarea
        data-testid="curate-rationale"
        className="hud-input text-sm min-h-[80px] resize-y"
        placeholder="AI rationale (quote from audit log)"
        value={form.rationale}
        onChange={(e) => setForm({ ...form, rationale: e.target.value })}
      />
      <textarea
        data-testid="curate-methodology-note"
        className="hud-input text-sm min-h-[80px] resize-y"
        placeholder="Why this teaches the methodology — what distinction does it illustrate?"
        value={form.methodology_note}
        onChange={(e) => setForm({ ...form, methodology_note: e.target.value })}
      />
      <div className="flex gap-2">
        <button
          data-testid="save-curate-btn"
          onClick={handle}
          disabled={submitting || !form.redacted_summary || !form.rationale}
          className="font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/80 disabled:opacity-40"
        >
          PUBLISH
        </button>
        <button onClick={onCancel} className="font-hud text-xs text-white/80 tracking-wider hover:text-[#2F8A92]">CANCEL</button>
      </div>
    </div>
  );
}

export default function MethodologyModerationPage() {
  const { user } = useAuth();
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [curating, setCurating] = useState(false);
  const [flags, setFlags] = useState([]);
  const isAdmin = user?.role === 'admin';

  const load = () => {
    setLoading(true);
    methodologyAPI.modExamples().then((r) => setExamples(r.data || [])).finally(() => setLoading(false));
    if (isAdmin) {
      methodologyAPI.modFlags().then((r) => setFlags(r.data || [])).catch(() => {});
    }
  };

  useEffect(() => { load(); }, [isAdmin]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this example?')) return;
    await methodologyAPI.removeExample(id);
    load();
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-3xl" data-testid="methodology-moderation-page">
      <div>
        <h2 className="font-historical text-[#D4A84A] text-2xl md:text-3xl font-light tracking-tight">
          Why Wasn't This Flagged?
        </h2>
        <p className="font-hud text-[10px] text-white/80 tracking-wider mt-1">
          MODERATION PHILOSOPHY ON DISPLAY · WHERE BEHAVIOR ENDS AND INTERPRETATION BEGINS
        </p>
      </div>

      <div className="border border-[#D4A84A]/15 rounded p-3 space-y-2">
        <p className="font-hud text-[11px] tracking-widest text-[#D4A84A] uppercase flex items-center gap-2">
          <Shield size={12} /> METHODOLOGY HOLD
        </p>
        <p className="font-mono text-[11px] text-[#e8d5a3] leading-relaxed italic">
          "AI moderates behavior, not ideas. Rating handles bad history."
        </p>
        <p className="font-mono text-[10px] text-white/80">
          This page documents real decisions — some things the platform flagged, some things it explicitly
          did not flag. Every example is curated from live moderation output. Text is redacted; the reasoning is preserved.
          You can see exactly what the platform considers a behavioral violation, and what it considers a scholarly dispute.
        </p>
      </div>

      {isAdmin && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-hud text-sm tracking-widest uppercase text-[#2F8A92] flex items-center gap-2">
              <Sparkles size={12} /> ADMIN — CURATE NEW EXAMPLE
            </h3>
            {!curating && (
              <button
                data-testid="new-example-btn"
                onClick={() => setCurating(true)}
                className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-2 py-1 hover:bg-[#D4A84A]/80 flex items-center gap-1"
              >
                <Plus size={10} /> NEW
              </button>
            )}
          </div>
          {curating && <CurateForm onCurated={() => { setCurating(false); load(); }} onCancel={() => setCurating(false)} />}
          {flags.length > 0 && (
            <details className="border border-[#2F8A92]/10 rounded p-2">
              <summary className="font-hud text-[10px] tracking-wider uppercase text-[#2F8A92] cursor-pointer flex items-center gap-1">
                <Flag size={10} /> RECENT LIVE FLAGS ({flags.length}) — for admin reference when curating
              </summary>
              <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
                {flags.slice(0, 20).map((f) => (
                  <div key={f.id} className="font-mono text-[9px] text-white/80 border-b border-[#2F8A92]/5 pb-1">
                    {f.context?.severity} · {(f.context?.categories || []).join(', ')} — {f.context?.rationale?.slice(0, 100)}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <div className="space-y-3">
        <h3 className="font-hud text-sm tracking-widest uppercase text-[#2F8A92] flex items-center gap-2">
          <MessageSquare size={12} /> EXAMPLES ({examples.length})
        </h3>
        {loading ? (
          <p className="font-hud text-xs text-[#52525B] animate-pulse">LOADING…</p>
        ) : examples.length === 0 ? (
          <p className="font-mono text-[11px] text-[#52525B]">
            No examples curated yet. The admin will populate this page as real moderation decisions accumulate.
          </p>
        ) : (
          examples.map((e) => (
            <div key={e.id} className="relative">
              <ExampleCard example={e} />
              {isAdmin && (
                <button
                  data-testid={`delete-example-${e.id}`}
                  onClick={() => handleDelete(e.id)}
                  className="absolute top-2 right-2 text-[#52525B] hover:text-red-400 transition-all"
                >
                  <Trash2 size={10} />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
